import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import WhatsAppChat from 'components/ui/WhatsAppChat';
import CallButton from 'components/ui/CallButton';
import ProgressIndicator from './components/ProgressIndicator';
import DateRoomSelection from './components/DateRoomSelection';
import GuestDetailsForm from './components/GuestDetailsForm';
import PaymentSection from './components/PaymentSection';
import BookingConfirmation from './components/BookingConfirmation';
import BookingSummaryPanel from './components/BookingSummaryPanel';

const BookingEngine = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState({
    // Step 1: Dates & Rooms
    checkIn: '',
    checkOut: '',
    roomType: '',
    guests: 2,
    activities: [],
    
    // Step 2: Guest Details
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'in',
    from: '',
    dietary: 'none',
    specialRequests: '',
    newsletter: false,
    agreeTerms: false,
    
    // Step 3: Payment
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingAddress: '',
    billingCity: '',
    billingPostal: ''
  });

  // Auto-save form data to localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('bookingFormData');
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error loading saved booking data:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('bookingFormData', JSON.stringify(formData));
  }, [formData]);

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleStepClick = (stepId) => {
    if (completedSteps?.includes(stepId)) {
      setCurrentStep(stepId);
    }
  };

  const handleNext = () => {
    if (!completedSteps?.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleNewBooking = () => {
    setCurrentStep(1);
    setCompletedSteps([]);
    setFormData({
      checkIn: '',
      checkOut: '',
      roomType: '',
      guests: 2,
      activities: [],
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: 'in',
      from: '',
      dietary: 'none',
      specialRequests: '',
      newsletter: false,
      agreeTerms: false,
      cardNumber: '',
      cardName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      billingAddress: '',
      billingCity: '',
      billingPostal: ''
    });
    localStorage.removeItem('bookingFormData');
  };

  const getBookingSummary = () => {
    const roomTypes = {
      'deluxe-forest-villa': { name: 'Deluxe Forest Villa', price: 4500 },
      'river-view-cottage': { name: 'River View Cottage', price: 3800 },
      'luxury-tent': { name: 'Luxury Tent', price: 2900 },
      'tree-house': { name: 'Tree House', price: 5200 }
    };

    const activityPrices = {
      'rafting': { name: 'River Rafting', price: 1200 },
      'safari': { name: 'Jungle Safari', price: 800 }
    };

    const room = roomTypes?.[formData?.roomType] || { name: 'Select Room', price: 0 };
    const nights = formData?.checkIn && formData?.checkOut ? 
      Math.ceil(Math.abs(new Date(formData.checkOut) - new Date(formData.checkIn)) / (1000 * 60 * 60 * 24)) : 0;
    
    const roomTotal = room?.price * nights;
    const activities = (formData?.activities || [])?.map(activity => activityPrices?.[activity])?.filter(Boolean);
    const activityTotal = activities?.reduce((sum, activity) => sum + (activity?.price * formData?.guests), 0);
    const subtotal = roomTotal + activityTotal;
    const taxes = Math.round(subtotal * 0.12);
    const total = subtotal + taxes;

    return {
      accommodation: room?.name,
      checkIn: formData?.checkIn,
      checkOut: formData?.checkOut,
      guests: formData?.guests,
      nights,
      roomRate: room?.price,
      activities,
      taxes,
      total
    };
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <DateRoomSelection
            formData={formData}
            onUpdate={updateFormData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <GuestDetailsForm
            formData={formData}
            onUpdate={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <PaymentSection
            formData={formData}
            onUpdate={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            bookingSummary={getBookingSummary()}
          />
        );
      case 4:
        return (
          <BookingConfirmation
            bookingData={formData}
            onNewBooking={handleNewBooking}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Book Your Stay - Dandeli Adventure Resorts | Secure Online Booking</title>
        <meta name="description" content="Complete your reservation at Dandeli Adventure Resorts with our secure booking engine. Choose dates, rooms, and activities for your perfect nature getaway." />
        <meta name="keywords" content="Dandeli resort booking, online reservation, adventure resort Karnataka, secure booking engine" />
      </Helmet>

      <Header />
      
      <main className="pt-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Complete Your Booking
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Secure your adventure at Dandeli Adventure Resorts with our simple booking process
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1 lg:pr-8">
              <ProgressIndicator
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
              />

              {renderCurrentStep()}
            </div>

            {/* Booking Summary Panel - Desktop Only */}
            <div className="lg:w-80">
              {/* This space is reserved for the sticky summary panel */}
            </div>
          </div>
        </div>

        {/* Booking Summary Panel */}
        <BookingSummaryPanel
          formData={formData}
          isVisible={currentStep < 4}
        />

        {/* Trust Signals Footer */}
        {currentStep < 4 && (
          <div className="bg-muted py-6 mt-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-success-foreground" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="font-medium text-card-foreground mb-1">Secure Booking</h3>
                  <p className="text-sm text-muted-foreground">SSL encrypted & PCI compliant</p>
                </div>

                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="font-medium text-card-foreground mb-1">Instant Confirmation</h3>
                  <p className="text-sm text-muted-foreground">Immediate booking confirmation</p>
                </div>

                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-secondary-foreground" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <h3 className="font-medium text-card-foreground mb-1">24/7 Support</h3>
                  <p className="text-sm text-muted-foreground">Round-the-clock assistance</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <WhatsAppChat />
      <CallButton />
    </div>
  );
};

export default BookingEngine;